import type {
	TAction,
	TActionFunc,
	TActionWithArgs,
	TActionWithOptionalArgs,
	TActionArgsMap,
	TArgOfAction,
	TInvocationTrigger,
} from "./types";

const boundActions = new Map<TAction, unknown[]>();

export function bindAction<A extends TAction>(
	action: A,
	handler: TActionFunc<A>,
) {
	const handlers = boundActions.get(action) ?? [];
	handlers.push(handler);
	boundActions.set(action, handlers);
}

export function unbindAction<A extends TAction>(
	action: A,
	handler: TActionFunc<A>,
) {
	const handlers = boundActions.get(action);
	if (!handlers) return;

	const remainingHandlers = handlers.filter((boundHandler) => boundHandler !== handler);
	if (remainingHandlers.length === 0) {
		boundActions.delete(action);
	} else {
		boundActions.set(action, remainingHandlers);
	}
}

type InvokeActionFunc = {
	(
		action: TActionWithOptionalArgs,
		args?: undefined,
		trigger?: TInvocationTrigger,
	): void;
	<A extends TActionWithArgs>(
		action: A,
		args: TActionArgsMap[A],
		trigger?: TInvocationTrigger,
	): void;
};

export const invokeAction: InvokeActionFunc = <A extends TAction>(
	action: A,
	args?: TArgOfAction<A>,
	trigger?: TInvocationTrigger,
) => {
	boundActions.get(action)?.forEach((handler) => {
		if (typeof handler === "function") {
			Reflect.apply(handler, undefined, [args, trigger]);
		}
	});
};
